import { z } from 'zod';
import { isoDay } from './values.js';
/**
 * Where a mandate - one cycle of a client's business relationship with the firm, from its first
 * intake to its end (ADR-078) - stands: its intake, an offer on its way, active, given notice,
 * ended. A client has one or more, one after the other, never two at once.
 */
export const MandatePhase = z
    .enum(['intake', 'offer', 'active', 'terminated', 'ended'])
    .meta({ id: 'MandatePhase' });
/**
 * Why a mandate ended: its contract ended after its last day, a consumer withdrew from it, or the
 * firm ended it (ADR-063). An intake cancelled without a contract ends none - the mandate rests.
 */
export const MandateEndReason = z
    .enum(['contract_ended', 'contract_revoked', 'firm'])
    .meta({ id: 'MandateEndReason' });
/**
 * What an intake is to its mandate: the due diligence of its beginning (`onboarding`) - also when
 * it carries on a mandate that rested - or an update of a mandate whose due diligence was done or
 * which began (`update`, ADR-050, ADR-062).
 */
export const IntakePurpose = z.enum(['onboarding', 'update']).meta({ id: 'IntakePurpose' });
/** States of a contract on its way to the client: drafted, offered or accepted, not yet in force. */
const ON_ITS_WAY = new Set(['draft', 'offered', 'accepted']);
/**
 * The phase of a mandate (ADR-078 § 3), the one rule for it: ended once its end is recorded; an
 * offer while a contract is on its way; active while one is in force, terminated once given
 * notice, ended once it ended or was withdrawn. Without a contract - or after an offer that came
 * to nothing - a completed intake is an active mandate (decision 2 of 2026-11-03), else it is in
 * its intake (decision 6).
 */
export function mandatePhaseOf(facts) {
    const { contract } = facts;
    if (facts.ended || contract === 'ended' || contract === 'revoked')
        return 'ended';
    if (contract !== null && ON_ITS_WAY.has(contract))
        return 'offer';
    if (contract === 'in_force')
        return 'active';
    if (contract === 'terminated')
        return 'terminated';
    return facts.intakeCompleted ? 'active' : 'intake';
}
/** The phases in the order of a mandate's way. */
const ORDER = MandatePhase.options;
/**
 * Whether a mandate may move from one phase to another (ADR-078 § 3) - the table the database's
 * trigger holds too: any step on, as the daily reconciliation may catch up on steps missed; back
 * from an offer that came to nothing to the intake; from active to an offer only while no
 * contract runs (a mandate without a contract in Kanos); and out of `ended` only where the firm
 * takes back an end it set itself.
 */
export function mandateMayMove(from, to, state) {
    if (from === to)
        return true;
    if (from === 'ended')
        return state.endedByFirm;
    if (ORDER.indexOf(to) > ORDER.indexOf(from))
        return true;
    if (from === 'offer' && to === 'intake')
        return true;
    return (from === 'active' && to === 'offer' && state.contract !== null && ON_ITS_WAY.has(state.contract));
}
/**
 * The purpose of an intake begun in a mandate (ADR-078 § 4): an update once the mandate's due
 * diligence was done or it began - or where the mandate carries an ended one on without a new
 * intake, whose identification still counts (§ 11 Abs. 3 GwG, decided 2026-11-03); its onboarding
 * before - also where it carries on a mandate that rested.
 */
export const intakePurposeOf = (mandate) => mandate.intakeCompleted || mandate.began || mandate.resumed
    ? 'update'
    : 'onboarding';
export const mandatePhaseLabel = {
    intake: 'Aufnahme',
    offer: 'Angebot',
    active: 'Aktiv',
    terminated: 'Gekündigt',
    ended: 'Beendet',
};
export const mandateEndReasonLabel = {
    contract_ended: 'Vertrag beendet',
    contract_revoked: 'Vertrag widerrufen',
    firm: 'Von der Kanzlei beendet',
};
/**
 * Where the client's newest mandate stands, for the client list (ADR-078 § 7): its phase, whether
 * it rests - its intake cancelled without a contract (decision 5) - and what runs beside it: an
 * intake of its onboarding, an update (decision 10). From the second on, its number is shown.
 */
export const MandateStanding = z
    .object({
    id: z.uuid(),
    number: z.int().positive(),
    phase: MandatePhase,
    resting: z.boolean(),
    intakeOpen: z.boolean(),
    updateOpen: z.boolean(),
    endedOn: isoDay.nullable(),
    endReason: MandateEndReason.nullable(),
})
    .meta({ id: 'MandateStanding' });
/**
 * How long after a mandate's end the firm may conclude a new contract without a new intake
 * (decision 8 of 2026-11-03, ADR-078 § 5): months from its last day, 0 to 12 - 0 asks a new
 * intake every time. Twelve unless an administrator chose a shorter time.
 */
export const MANDATE_RESUMPTION_DEFAULT_MONTHS = 12;
export const MandateResumption = z
    .object({ months: z.int().min(0).max(12) })
    .meta({ id: 'MandateResumption' });
/**
 * The last day a mandate ended on `endedOn` may be carried on by a new contract: so many calendar
 * months after its last day, at the end of a shorter month its last day; `null` for 0 months.
 */
export function resumableUntil(endedOn, months) {
    if (months <= 0)
        return null;
    const [year, month, day] = endedOn.split('-').map(Number);
    const target = new Date(Date.UTC(year, month - 1 + months, 1));
    const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0));
    target.setUTCDate(Math.min(day, last.getUTCDate()));
    return target.toISOString().slice(0, 10);
}
//# sourceMappingURL=mandates.js.map